import React, {
  useRef,
  useState,
  RefObject,
  forwardRef,
  useImperativeHandle,
  useEffect,
  ForwardRefRenderFunction,
} from 'react';
import { FormControl, FormHelperText, Grid, Typography } from '@mui/material';

import Input from '../../../../Common/InputComponents/Input';
import Switch, { SwitchRef } from 'components/Common/InputComponents/Switch';

import { AlarmDefinition } from 'types/types';
import { RulesFormValidationError } from '../CreateForm';

export interface Ref {
  dayOverDay: RefObject<SwitchRef>;
  toggleShowInput: (show: boolean) => void;
  studyIntervals: RefObject<HTMLInputElement>;
  toggleShowUseDayOverDayToggle: (show: boolean) => void;
}

interface Prop {
  alarmDefinitionData: AlarmDefinition | null;
  validationErrors: RulesFormValidationError;
}

const StudyIntervals: ForwardRefRenderFunction<Ref, Prop> = (
  { alarmDefinitionData, validationErrors },
  ref
) => {
  const [show, setShow] = useState<boolean>(false);
  const [showUseDayOverDay, setShowUseDayOverDay] = useState<boolean>(false);
  const [localValidationError, setLocalValidationError] = useState<
    string | null
  >(validationErrors.studyIntervals);

  const dayOverDayRef = useRef<SwitchRef>(null);
  const studyIntervalsRef = useRef<HTMLInputElement>(null);

  const toggleShowInput = (show: boolean) => setShow(show);
  const toggleShowUseDayOverDayToggle = (show: boolean) =>
    setShowUseDayOverDay(show);

  const handleInputChange = () => {
    // Clear local validation error when input changes
    setLocalValidationError(null);
  };

  useImperativeHandle(ref, () => ({
    dayOverDay: dayOverDayRef,
    toggleShowUseDayOverDayToggle,
    toggleShowInput: toggleShowInput,
    studyIntervals: studyIntervalsRef,
  }));

  useEffect(() => {
    if (alarmDefinitionData?.study_intervals) {
      setShow(true);
    }
    if (
      !['monthly', 'weekly'].includes(
        alarmDefinitionData?.time_aggregation_level.toLowerCase() ?? ''
      ) &&
      alarmDefinitionData?.execution_rules_serialized?.useDayOverDay
    ) {
      setShowUseDayOverDay(true);
    } else {
      setShowUseDayOverDay(false);
    }
  }, [alarmDefinitionData]);

  // Synchronize local validation error state with parent-provided validation error
  useEffect(() => {
    setLocalValidationError(validationErrors.studyIntervals);
  }, [validationErrors.studyIntervals]);

  if (show) {
    return (
      <React.Fragment>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!localValidationError}>
            <React.Fragment>
              <Typography className="form-element-label">
                Study Intervals
              </Typography>
              <Input
                size="small"
                className="input-elements"
                id="outlined-multiline-static"
                value={alarmDefinitionData?.study_intervals ?? ''}
                inputRef={studyIntervalsRef}
                handleOnChange={handleInputChange}
                type="number"
              />
            </React.Fragment>
            <FormHelperText>{localValidationError}</FormHelperText>
          </FormControl>
        </Grid>
        {showUseDayOverDay && (
          <Grid item xs={4}>
            <Typography className="form-element-label">
              Use Day Over Day Intervals
            </Typography>
            <FormControl fullWidth className="day-over-day-container">
              <Switch
                value={
                  alarmDefinitionData?.execution_rules_serialized?.useDayOverDay
                }
                ref={dayOverDayRef}
                className={`day-over-day-switch`}
              />
            </FormControl>
          </Grid>
        )}
      </React.Fragment>
    );
  } else {
    return <React.Fragment />;
  }
};

export default forwardRef<Ref, Prop>(StudyIntervals);
